import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Avatar,
  Checkbox,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  IconButton,
} from "@mui/material";
import { LuDownload } from "react-icons/lu";
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import { MdNotificationsActive } from "react-icons/md";
import { scheduleType, notificationType } from "../../configs/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import {
  sendNotification,
  updateNotification,
  getUserNotifications,
} from "../../api/notificationService";
import { useLocation, useNavigate } from "react-router-dom";
import auth from "../../configs/auth";

const normalizeContent = (text) =>
  (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");

const fieldLabelSx = {
  fontWeight: 600,
  color: "#374151",
  mb: 0.75,
  display: "block",
  fontSize: "0.875rem",
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    "&:hover fieldset": { borderColor: "#752ADA" },
    "&.Mui-focused fieldset": { borderColor: "#752ADA" },
  },
};

function NotificationPreview({ title, content, imageUrl }) {
  return (
    <Box
      sx={{
        maxWidth: 320,
        mx: "auto",
        borderRadius: "28px",
        border: "8px solid #1a1a2e",
        background: "linear-gradient(160deg, #f8f9ff 0%, #eef0ff 100%)",
        overflow: "hidden",
        boxShadow: "0 20px 40px rgba(117, 42, 218, 0.15)",
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
          color: "#fff",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MdNotificationsActive size={18} />
        <Typography variant="caption" fontWeight={600} letterSpacing={0.5}>
          NOTIFICATION PREVIEW
        </Typography>
      </Box>
      <Box sx={{ p: 2.5, minHeight: 220 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "14px",
            border: "1px solid #e8e0f7",
            background: "#fff",
          }}
        >
          {imageUrl && (
            <Box
              component="img"
              src={imageUrl}
              alt="preview"
              sx={{
                width: "100%",
                maxHeight: 120,
                objectFit: "cover",
                borderRadius: "10px",
                mb: 1.5,
              }}
            />
          )}
          <Typography
            fontWeight={700}
            fontSize="0.95rem"
            color="#1a1a2e"
            mb={content ? 1 : 0}
          >
            {title || "Notification Title"}
          </Typography>
          <Typography
            variant="body2"
            color="#4b5563"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.65,
              fontSize: "0.85rem",
            }}
          >
            {content || ""}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}

export default function CreateNotification(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    header,
    titlename,
    contentname,
    notificationType: propNotificationType,
    button_name = "Add",
    row,
  } = { ...props, ...location.state };

  const userData = JSON.parse(localStorage.getItem("adminUserData"));
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const jeweller = sessionStorage.getItem(auth.selectedJeweller);
  const jewellerID = storedUser?.jeweller;
  const finalJewellerId =
    jeweller && jeweller.trim() !== "" ? jeweller : jewellerID;

  const [notification, setNotification] = useState({
    title: row?.title || "",
    content: row?.body || "",
    notificationType: row?.notificationType || propNotificationType,
    scheduleType: row?.scheduleType || scheduleType.now,
    scheduleAt: row?.scheduleAt || Date.now(),
    jeweller: row?.jeweller || userData.jeweller,
    user: row?.user || userData._id,
    notificationimage: row?.url || "",
  });
  const [saveTemplate, setSaveTemplate] = useState(row?.savedTemplate || false);
  const [templates, setTemplates] = useState([]);
  const [isTemplateSelected, setIsTemplateSelected] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const previewImageUrl =
    notification.notificationimage instanceof File
      ? URL.createObjectURL(notification.notificationimage)
      : notification.notificationimage || null;

  async function fetchTemplates() {
    const response = await getUserNotifications({
      jeweller: finalJewellerId,
      user: userData._id,
      notificationType: notificationType.admin,
    });
    if (response.success) {
      setTemplates(response.data.filter((t) => t.savedTemplate));
    }
  }

  useEffect(() => {
    fetchTemplates();
  }, []);

  const buildFormData = (isUpdate = false) => {
    const form_data = new FormData();
    const normalizedContent = normalizeContent(notification.content);

    form_data.append("title", notification.title);
    form_data.append(
      isUpdate ? "body" : "content",
      normalizedContent,
    );
    form_data.append("notificationType", notification.notificationType);
    form_data.append("scheduleType", notification.scheduleType);
    form_data.append("scheduleAt", notification.scheduleAt);
    form_data.append("savedTemplate", saveTemplate);

    if (isUpdate && row?._id) {
      form_data.append("id", row._id.toString());
    }
    if (!isUpdate) {
      if (notification._id) {
        form_data.append("id", notification._id.toString());
      }
      form_data.append("jeweller", notification.jeweller);
      form_data.append("user", notification.user);
      form_data.append("notificationimage", notification.notificationimage);
    }

    return form_data;
  };

  async function updateNoti() {
    if (!notification.title || !notification.content) {
      return alert("please provide title or content");
    }

    const response = await updateNotification(buildFormData(true));

    if (response.success) {
      alert(response.message);
      navigate(-1);
    } else {
      return alert(response.message);
    }
  }

  async function saveNotification() {
    if (!notification.title || !notification.content) {
      return alert("please provide title or content");
    }

    const response = await sendNotification({ data: buildFormData(false) });

    if (response.success) {
      alert(response.message);
      navigate(-1);
    } else {
      return alert(response.message);
    }
  }

  const handleSubmit = () => {
    if (row?._id && !isTemplateSelected) {
      updateNoti();
    } else {
      saveNotification();
    }
  };

  return (
    <Box
      sx={{
        background: "#f3f3fd",
        minHeight: "100vh",
        width: "100%",
        p: 2,
        mt: 5,
        boxSizing: "border-box",
      }}
    >
      <Paper
        sx={{
          width: "100%",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(117, 42, 218, 0.08)",
        }}
        elevation={0}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
            color: "#fff",
            px: { xs: 2, sm: 3 },
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: "#fff", background: "rgba(255,255,255,0.15)", "&:hover": { background: "rgba(255,255,255,0.25)" } }}
            size="small"
          >
            <IoIosArrowBack />
          </IconButton>
          <Box>
            <Typography fontWeight={700} fontSize={{ xs: 20, sm: 24 }} fontFamily="Aeonik">
              {header}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Templates */}
          {header !== "Update Notification" && templates.length > 0 && (
            <Box mb={4}>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5} color="#374151">
                Saved Templates
              </Typography>
              <Stack direction="row" spacing={2} sx={{ overflowX: "auto", pb: 1 }}>
                {templates.map((template) => (
                  <Card
                    key={template._id}
                    variant="outlined"
                    sx={{
                      minWidth: 260,
                      borderRadius: "14px",
                      borderColor: "#e8e0f7",
                      transition: "all 0.2s",
                      "&:hover": { borderColor: "#752ADA", boxShadow: "0 4px 16px rgba(117,42,218,0.1)" },
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {template.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          whiteSpace: "pre-wrap",
                          mt: 0.5,
                          mb: 1.5,
                        }}
                      >
                        {template.body}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        sx={{
                          background: "#752ADA",
                          textTransform: "none",
                          borderRadius: "8px",
                          boxShadow: "none",
                          "&:hover": { background: "#5f1fb8", boxShadow: "none" },
                        }}
                        onClick={() => {
                          setNotification({
                            _id: template._id,
                            title: template.title,
                            content: template.body,
                            notificationType: template.notificationType,
                            scheduleType: template.scheduleType,
                            scheduleAt: template.scheduleAt,
                            jeweller: template.jeweller,
                            user: template.user,
                            notificationimage: template.url,
                          });
                          setSaveTemplate(true);
                          setIsTemplateSelected(true);
                          if (template.scheduleType === scheduleType.now) {
                            setOpenConfirm(true);
                          }
                        }}
                      >
                        Use Template
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          <Grid container spacing={3} alignItems="flex-start">
            {/* Form */}
            <Grid size={{ xs: 12, lg: 7 }}>
              <Stack spacing={3}>
                <Box>
                  <FormLabel sx={fieldLabelSx}>{titlename}</FormLabel>
                  <TextField
                    size="small"
                    fullWidth
                    value={notification.title}
                    onChange={(e) =>
                      setNotification({ ...notification, title: e.target.value })
                    }
                    sx={textFieldSx}
                  />
                </Box>

                <Box>
                  <FormLabel sx={fieldLabelSx}>{contentname}</FormLabel>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={10}
                    maxRows={20}
                    value={notification.content}
                    onChange={(e) =>
                      setNotification({ ...notification, content: e.target.value })
                    }
                    sx={{
                      ...textFieldSx,
                      "& .MuiInputBase-input": {
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        fontFamily: "inherit",
                      },
                    }}
                  />
                </Box>

                <Divider />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: notification.scheduleType === scheduleType.later ? 6 : 12 }}>
                    <FormLabel sx={fieldLabelSx}>Schedule Type</FormLabel>
                    <RadioGroup
                      name="now-later"
                      value={notification.scheduleType}
                      onChange={(e) =>
                        setNotification({
                          ...notification,
                          scheduleType: e.target.value,
                        })
                      }
                      row
                    >
                      <FormControlLabel
                        value={scheduleType.now}
                        control={<Radio sx={{ color: "#752ADA", "&.Mui-checked": { color: "#752ADA" } }} />}
                        label="Send Now"
                      />
                      <FormControlLabel
                        value={scheduleType.later}
                        control={<Radio sx={{ color: "#752ADA", "&.Mui-checked": { color: "#752ADA" } }} />}
                        label="Schedule Later"
                      />
                    </RadioGroup>
                  </Grid>

                  {notification.scheduleType === scheduleType.later && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormLabel sx={fieldLabelSx}>Schedule Date & Time</FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          label="Select Date & Time"
                          format="DD/MM/YYYY hh:mm A"
                          value={dayjs(notification.scheduleAt)}
                          onChange={(newValue) =>
                            setNotification({
                              ...notification,
                              scheduleAt: newValue.valueOf(),
                            })
                          }
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: textFieldSx,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                </Grid>

                <Box>
                  <FormLabel sx={fieldLabelSx}>Notification Image (optional)</FormLabel>
                  <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        color: "#752ADA",
                        borderColor: "#c9a8f5",
                        textTransform: "none",
                        borderRadius: "10px",
                        "&:hover": { borderColor: "#752ADA", background: "#faf5ff" },
                      }}
                    >
                      <LuDownload style={{ marginRight: 8 }} />
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (
                            file &&
                            (file.type === "image/png" || file.type === "image/jpeg")
                          ) {
                            setNotification({
                              ...notification,
                              notificationimage: file,
                            });
                          } else {
                            alert("Please upload only PNG or JPG file");
                            e.target.value = "";
                          }
                        }}
                      />
                    </Button>
                    {previewImageUrl && (
                      <Avatar
                        src={previewImageUrl}
                        alt="preview"
                        variant="rounded"
                        sx={{ width: 56, height: 56, borderRadius: "10px" }}
                      />
                    )}
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveTemplate}
                      onChange={(e) => setSaveTemplate(e.target.checked)}
                      sx={{ color: "#752ADA", "&.Mui-checked": { color: "#752ADA" } }}
                    />
                  }
                  label="Save as Template for future use"
                />
              </Stack>
            </Grid>

            {/* Preview */}
            <Grid size={{ xs: 12, lg: 5 }}>
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: 24,
                  background: "linear-gradient(180deg, #faf8ff 0%, #f3f3fd 100%)",
                  borderRadius: "16px",
                  p: 3,
                  border: "1px solid #e8e0f7",
                  height: "100%",
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} mb={2} color="#374151">
                  Live Preview
                </Typography>
                <NotificationPreview
                  title={notification.title}
                  content={notification.content}
                  imageUrl={previewImageUrl}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, pt: 2, borderTop: "1px solid #eee" }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                mr: 2,
                textTransform: "none",
                borderRadius: "10px",
                borderColor: "#d1d5db",
                color: "#6b7280",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #752ADA 0%, #9b4dff 100%)",
                fontSize: "16px",
                px: 4,
                py: 1.25,
                textTransform: "none",
                borderRadius: "10px",
                boxShadow: "0 4px 14px rgba(117, 42, 218, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5f1fb8 0%, #7a3dd4 100%)",
                  boxShadow: "0 6px 20px rgba(117, 42, 218, 0.4)",
                },
              }}
              onClick={handleSubmit}
            >
              {button_name} <IoIosArrowForward style={{ marginLeft: 4 }} />
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Confirm Dialog */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        PaperProps={{ sx: { borderRadius: "16px", maxWidth: 420 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Notification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Are you sure you want to send this notification now?
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: "12px", borderColor: "#e8e0f7", background: "#faf8ff" }}
          >
            <Typography fontWeight={700} fontSize="0.9rem" mb={1}>
              {notification.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}
            >
              {notification.content}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenConfirm(false)}
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              saveNotification();
              setOpenConfirm(false);
            }}
            variant="contained"
            autoFocus
            sx={{
              textTransform: "none",
              background: "#752ADA",
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { background: "#5f1fb8" },
            }}
          >
            Send Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
